import validator from 'validator';
import EventBus from 'eventing-bus';
import Box from '@material-ui/core/Box';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import AppBar from '@material-ui/core/AppBar';
import TextField from '@mui/material/TextField';
import React, { useState, useEffect } from 'react';
import Typography from '@material-ui/core/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import { useDispatch, useSelector } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';

import { createMember, updateMember, deleteMember, setNewWorkspaceModal, updateWorkspace, getPendingInvites, resendInvite } from '../../../store/Settings/index.js';

// All available role options
const allRoleOptions = [
    { value: 'owner', label: 'Owner', detail: <div className='tags-postion'><span>All permissions</span> </div> },
    { value: 'admin', label: 'Admin', detail: <div className='tags-postion'><span>Manage team,</span><span> billing and settings,</span><span>but not delete the workspace</span> </div> },
    { value: 'editor', label: 'Editor', detail: <div className='tags-postion'><span>Manage everything except team and billing</span> </div> },
    { value: 'view', label: 'View Access', detail: <div className='tags-postion'><span>Reply to Unibox messages and update email account settings</span> </div> },
]

// Function to get available role options based on current user role
const getAvailableRoleOptions = (currentUserRole) => {
    if (currentUserRole === 'owner') return allRoleOptions; // Owner can assign all roles
    else if (currentUserRole === 'admin') return allRoleOptions.filter(option => option['value'] !== 'owner'); // Admin cannot assign owner role
    else if (currentUserRole === 'editor') return allRoleOptions.filter(option => !['owner', 'admin'].includes(option['value'])); // Editor can only assign editor and view
    else return []; // View role cannot assign any roles
}

const Workspace = () => {
    const dispatch = useDispatch();
    const { currentWorkspace, pendingInvites } = useSelector(state => state['Settings']);
    
    const [value, setValue] = useState(0);
    const [workspaceData, setWorkspaceData] = useState({});
    const [newMember, setNewMember] = useState({ email: '', role: '', workspaceId: '' });

    useEffect(() => {
        if (currentWorkspace && currentWorkspace['_id']) {
            setWorkspaceData(currentWorkspace);
            setNewMember({ ...newMember, workspaceId: currentWorkspace['_id'] });
            if (currentWorkspace['role'] === 'owner' || currentWorkspace['role'] === 'admin')
                dispatch(getPendingInvites(currentWorkspace['_id']));
        }
    }, [currentWorkspace]);

    const handleChange = e => setWorkspaceData(st => ({ ...st, [e['target'] && e['target']['name']]: e['target'] && e['target']['value'] }))

    const onCreateMember = () => {
        if (!newMember['email'] || !newMember['role']) return EventBus.publish('error', 'Please provide email and role');
        if (!validator.isEmail(newMember['email'])) return EventBus.publish('error', 'Please provide valid email');
        dispatch(createMember(newMember))
        setNewMember({ ...newMember, email: '', role: '' });
    }

    const onUpdateMember = (e, newValue, index) => {
        let members = workspaceData['members'];
        members[index]['role'] = newValue['value'];
        setWorkspaceData({ ...workspaceData, members })
        dispatch(updateMember({ ...members[index], role: newValue?.['value'], workspaceId: workspaceData['_id'] }))
    }

    // Check if current workspace is a child workspace (has parentWorkspaceId)
    const isChildWorkspace = currentWorkspace && (currentWorkspace['parentWorkspaceId'] || currentWorkspace['isChildWorkspace']);

    return (
        <div className='tabs-content'>
            <div className='title-top-block'>
                <div className='left-area'>
                    <h2>Workspace {isChildWorkspace && <span className="badge badge-secondary ml-2">Sub-Workspace</span>}</h2>
                </div>
                <div className='right-btns'>
                    {!isChildWorkspace && (
                        <button className='btn-style-border' onClick={() => dispatch(setNewWorkspaceModal(true))}>Create New Workspace</button>
                    )}
                </div>
            </div>
            {/* Workspace Name Card */}
            <div className='settings-card'>
                <div className='settings-card__header'>
                    <div className='settings-card__header-icon'><i className='fa fa-briefcase'></i></div>
                    <div className='settings-card__header-text'>
                        <h4 className='settings-card__title'>Workspace Name</h4>
                        <p className='settings-card__description'>Your organization's workspace identity</p>
                    </div>
                </div>
                <ValidatorForm className='validator-form'>
                    <div className='row'>
                        <div className='col-12'>
                            <div className='group-form'>
                                <TextValidator
                                    required fullWidth name='name' type='text' margin='dense' variant='outlined'
                                    onChange={handleChange} validators={['required']} value={workspaceData['name']}
                                    className='login-input-fields' placeholder='Enter Workspace name'
                                    errorMessages={['Workspace name can not be empty']}
                                    onBlur={() => dispatch(updateWorkspace(workspaceData))}
                                />
                            </div>
                            <div className='fowrkspaceid-group'>
                                <h4>Workspace ID:
                                    <CopyToClipboard text={workspaceData['id']} onCopy={() => EventBus.publish('success', 'Address Copied!')}>
                                        <span className='copy-btn'> <i className='icon fa fa-copy'></i> </span>
                                    </CopyToClipboard>
                                </h4>
                                <TextValidator
                                    required fullWidth type='text' margin='dense' variant='outlined' name='workspaceId'
                                    value={workspaceData['id']} className='connections-input-fields'
                                    placeholder={`Please provide Assistant ID`}
                                />
                            </div>
                        </div>
                    </div>
                </ValidatorForm>
            </div>

            {/* Invite Members Card */}
            {['admin', 'owner', 'editor'].includes(currentWorkspace?.role) && (
                <div className='settings-card'>
                    <div className='settings-card__header'>
                        <div className='settings-card__header-icon'><i className='fa fa-user-plus'></i></div>
                        <div className='settings-card__header-text'>
                            <h4 className='settings-card__title'>Invite Members</h4>
                            <p className='settings-card__description'>Add team members to your workspace</p>
                        </div>
                    </div>
                    <ValidatorForm className='validator-form'>
                        <div className='row'>
                            <div className='col-lg-5 col-md-6'>
                                <div className='group-form'>
                                    <TextValidator
                                        required fullWidth type='email' name='email' margin='dense' variant='outlined'
                                        validators={['required']} value={newMember['email']}
                                        className='login-input-fields' placeholder='Enter email address'
                                        errorMessages={['email address can not be empty']}
                                        onChange={(e) => setNewMember({ ...newMember, email: e['target']['value'] })}
                                    />
                                </div>
                            </div>
                            <div className='col-lg-5 col-md-6'>
                                <div className='group-form'>
                                    <Autocomplete
                                        sx={{ minWidth: 200 }}
                                        options={getAvailableRoleOptions(currentWorkspace['role'])}
                                        componentsProps={{ paper: { className: 'members-dropdown', sx: { width: 230 } } }}
                                        getOptionLabel={(optionsMembers) => optionsMembers['label'] || ''}
                                        value={getAvailableRoleOptions(currentWorkspace['role']).find((opt) => opt['value'] === newMember['role']) || {}}
                                        onChange={(e, newValue) => setNewMember({ ...newMember, role: newValue?.['value'] || '' })}
                                        renderOption={(props, optionsMembers) => (
                                            <Box component='li' {...props} sx={{ display: 'block', alignItems: 'center', gap: 1 }}>
                                                <Typography style={{ fontSize: '14px' }}>{optionsMembers['label']}</Typography>
                                                {optionsMembers['detail']}
                                            </Box>
                                        )}
                                        renderInput={(params) => {
                                            const selectedOption = getAvailableRoleOptions(currentWorkspace['role']).find(opt => opt['value'] === params['inputProps']['value']);
                                            return (
                                                <TextField
                                                    {...params} variant='outlined' placeholder='Select Role'
                                                    InputLabelProps={{ shrink: false }}
                                                    InputProps={{ ...params['InputProps'], startAdornment: selectedOption ? <i style={{ marginRight: '10px' }}>{selectedOption['icon']}</i> : null }}
                                                />
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                            <div className='col-lg-2 col-md-6'>
                                <div className='group-form'>
                                    <button className='btn-style-new' type='button' onClick={onCreateMember}>Invite</button>
                                </div>
                            </div>
                        </div>
                    </ValidatorForm>
                </div>
            )}

            {/* Team Members Card */}
            <div className='settings-card' style={{ padding: 0, overflow: 'hidden' }}>
                <div className='settings-card__header' style={{ padding: '16px 24px 0', borderBottom: 'none', marginBottom: 0 }}>
                    <div className='settings-card__header-icon'><i className='fa fa-users'></i></div>
                    <div className='settings-card__header-text'>
                        <h4 className='settings-card__title'>Team</h4>
                    </div>
                </div>
                <div className='bottom-content-area'>
                    {['admin', 'owner', 'editor'].includes(currentWorkspace?.role) && (
                        <div className='tabs-box'>
                            <div className='tabs-email'>
                                <AppBar position='static'>
                                    <Tabs value={value} onChange={(e, value) => {setValue(value); window.scrollTo(0, 0)}} aria-label='simple tabs example'>
                                        <Tab label={`Team Members (${workspaceData?.members?.length || 0})`} />
                                        <Tab label={`Pending Invitations (${pendingInvites?.length || 0})`} />
                                    </Tabs>
                                </AppBar>
                            </div>
                        </div>
                    )}

                    <div className='tabs-content'>
                        <TabPanel value={value} index={0}>
                            <div className='team-member-panel'>
                                {workspaceData['members'] && workspaceData['members'].map((member, index) => (
                                    <div className='team-box'>
                                        <div className='left-area-team'>
                                            <h4>{member['firstName'] ? member['firstName'] + ' ' + member['lastName'] : member['email']}</h4>
                                            <p>{member['email']}</p>
                                        </div>
                                        <div className='right-area-team'>
                                            {['owner', 'admin', 'editor'].includes(currentWorkspace['role']) ? (
                                                <div className='right-dropdown'>
                                                    <Autocomplete
                                                        sx={{ minWidth: 200 }}
                                                        options={getAvailableRoleOptions(currentWorkspace['role'])}
                                                        onChange={(e, val) => onUpdateMember(e, val, index)}
                                                        getOptionLabel={(optionsMembers) => optionsMembers['label']}
                                                        componentsProps={{ paper: { className: 'members-dropdown', sx: { width: 230 } } }}
                                                        value={getAvailableRoleOptions(currentWorkspace['role']).find((opt) => opt['value'] == member['role']) || { label: member['role'] }}
                                                        renderOption={(props, optionsMembers) => (
                                                            <Box component='li' {...props} sx={{ display: 'block', alignItems: 'center', gap: 1 }}>
                                                                <Typography style={{ fontSize: '14px' }}>{optionsMembers['label']}</Typography>
                                                                {optionsMembers['detail']}
                                                            </Box>
                                                        )}
                                                        renderInput={(params) => {
                                                            const selectedOption = getAvailableRoleOptions(currentWorkspace['role']).find((opt) => opt['label'] === params['inputProps']['value']);
                                                            return (
                                                                <TextField
                                                                    {...params} variant='outlined' placeholder='Select Role'
                                                                    InputProps={{ ...params['InputProps'], startAdornment: selectedOption ? <i style={{ marginRight: '10px' }}>{selectedOption['icon']}</i> : null }}
                                                                />
                                                            );
                                                        }}
                                                    />
                                                    <button className='delete-btn' type='button' onClick={() => dispatch(deleteMember({ userId: member['_id'], workspaceId: workspaceData['_id'] }))}>Delete</button>
                                                </div>
                                            ) : <p>{member['role'].toUpperCase()}</p>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </TabPanel>

                        <TabPanel value={value} index={1}>
                            <div className='team-member-panel'>
                                {pendingInvites.length > 0 ? (
                                    pendingInvites.map(member => (
                                        <div className='team-box' key={member['userId']}>
                                            <div className='left-area-team'>
                                                <h4>{member['name']}</h4>
                                                <p>{member['email']}</p>
                                                <p>Invited By: {member['invitedBy']}</p>
                                            </div>
                                            <div className='right-area-team'>
                                                <div className='right-dropdown'>
                                                    <p>{member['role']?.toUpperCase()}</p>
                                                    <button type='button' className='btn-style-new' onClick={() => dispatch(resendInvite({ workspaceId: workspaceData['_id'], userId: member['userId'] }))}>Resend Invite</button>
                                                    <button type='button' className='delete-btn' onClick={() => dispatch(deleteMember({ userId: member['userId'], workspaceId: workspaceData['_id'] }))}>Delete</button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : <div className='team-member-panel style-empty'><div className='team-box'><p>No pending members found.</p></div></div>}
                            </div>
                        </TabPanel>
                    </div>
                </div>
            </div>
        </div>
    )
}

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div role='tabpanel' hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
            {value === index && <Box p={3}><Typography>{children}</Typography></Box>}
        </div>
    )
}

export default Workspace;